import { expData } from '@/data/expData'
import ticketData from '@/data/ticketData.json'

export class ExpCalculator {
  constructor() {
    this.expData = expData
    this.ticketData = ticketData
  }

  /**
   * 處理燃燒模式下的等級提升
   * @param {number} currentLevel - 當前等級
   * @param {string} burningMode - 燃燒模式
   * @returns {{newLevel: number, burningBonus: number}} 新等級和燃燒獎勵等級
   */
  processBurningLevelUp(currentLevel, burningMode) {
    if (!burningMode || currentLevel >= 260) {
      return { newLevel: currentLevel, burningBonus: 0 }
    }

    const burningLevels = burningMode === 'burning1+2' ? 2 : 4
    let newLevel = currentLevel
    let burningBonus = 0

    // 如果當前等級加上燃燒等級會超過260
    if (currentLevel + burningLevels > 260) {
      burningBonus = 260 - currentLevel
      newLevel = 260
    } else {
      burningBonus = burningLevels
      newLevel = currentLevel + burningLevels
    }

    return { newLevel, burningBonus }
  }

  /**
   * 計算使用秘藥後的結果
   * @param {number} currentLevel - 當前等級
   * @param {string} currentExpValue - 當前經驗值（帶千分位格式）
   * @param {Array} potions - 秘藥陣列
   * @param {string} burningMode - 燃燒模式 ('burning1+2' 或 'burning1+4')
   * @returns {Object|null} 計算結果或 null
   */
  calculateResult(currentLevel, currentExpValue, potions, burningMode = null) {
    if (!currentLevel || !currentExpValue) return null

    let totalExp = BigInt(String(currentExpValue).replace(/,/g, ''))
    let level = currentLevel
    let burningBonus = 0
    let currentBurningMode = burningMode

    // 計算每種秘藥提供的經驗值
    potions.forEach(potion => {
      if (potion.count > 0) {
        const maxLevel = potion.maxLevel
        for (let i = 0; i < potion.count; i++) {
          if (level >= 260) {
            // 如果已經達到260級，不再套用燃燒效果
            currentBurningMode = null
          }

          // 計算經驗值獲得
          if (potion.name === '究極聯盟成長秘藥' && potion.expSum) {
            totalExp += BigInt(potion.expSum)
          } else if (level <= maxLevel) {
            totalExp += BigInt(this.expData[level])
          } else {
            totalExp += BigInt(this.expData[maxLevel])
          }

          // 檢查是否升級
          while (level < 300 && totalExp >= BigInt(this.expData[level])) {
            totalExp -= BigInt(this.expData[level])
            level++

            // 每升1等都判斷一次燃燒
            if (currentBurningMode && level < 260) {
              const burningResult = this.processBurningLevelUp(level, currentBurningMode)
              burningBonus += burningResult.burningBonus
              level = burningResult.newLevel

              // 如果達到260級，清除燃燒模式
              if (level >= 260) {
                currentBurningMode = null
              }
            }
          }
        }
      }
    })

    return {
      level: level,
      exp: Number(totalExp),
      levelExp: this.expData[level],
      percentage: (Number(totalExp) / this.expData[level]) * 100,
      burningBonus: burningBonus
    }
  }

  /**
   * 計算當前等級所需經驗值
   * @param {number} level - 等級
   * @returns {number} 所需經驗值
   */
  getLevelExp(level) {
    return level ? this.expData[level] : 0
  }

  /**
   * 計算經驗值百分比
   * @param {number} level - 等級
   * @param {string|number} expValue - 經驗值（帶千分位格式或數字）
   * @returns {number} 經驗值百分比
   */
  calculateExpPercentage(level, expValue) {
    if (!level || expValue === null || expValue === undefined) return 0
    
    // 確保 expValue 是字串類型
    const strValue = String(expValue)
    const value = parseFloat(strValue.replace(/,/g, ''))
    
    if (isNaN(value)) return 0
    const levelExp = this.getLevelExp(level)
    if (!levelExp) return 0
    
    return (value / levelExp) * 100
  }

  /**
   * 格式化數字（添加千分位）
   * @param {number} num - 要格式化的數字
   * @returns {string} 格式化後的數字字串
   */
  formatNumber(num) {
    if (num === undefined || num === null || isNaN(num)) return '0'
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
  }

  /**
   * 驗證等級是否有效
   * @param {number} level - 要驗證的等級
   * @returns {boolean} 是否有效
   */
  validateLevel(level) {
    return level >= 200 && level <= 300
  }

  /**
   * 驗證經驗值百分比是否有效
   * @param {number} percentage - 要驗證的百分比
   * @returns {boolean} 是否有效
   */
  validatePercentage(percentage) {
    return percentage >= 0 && percentage <= 100
  }

  /**
   * 處理經驗值溢出，將多餘經驗自動升級
   * @param {number} level - 當前等級
   * @param {number} exp - 當前經驗值
   * @returns {{level: number, exp: number}}
   */
  processExpOverflow(level, exp) {
    while (level < 300 && exp >= this.expData[level]) {
      exp -= this.expData[level];
      level++;
    }
    return { level, exp };
  }

  /**
   * 修正版：燃燒跳級時每一級都扣除經驗值
   * @param {number} level - 當前等級
   * @param {BigInt} totalExp - 當前總經驗值
   * @param {string} burningMode - 燃燒模式
   * @returns {{newLevel: number, newExp: BigInt, burningBonus: number}} 新等級、新經驗值和燃燒獎勵等級
   */
  processBurningLevelUpWithExp(level, totalExp, burningMode) {
    if (!burningMode || level >= 260) {
      return { newLevel: level, newExp: totalExp, burningBonus: 0 }
    }
    const burningLevels = burningMode === 'burning1+2' ? 2 : 4
    let burningBonus = 0
    let newLevel = level
    let newExp = totalExp
    for (let i = 0; i < burningLevels; i++) {
      if (newLevel >= 260) break
      if (newExp >= BigInt(this.expData[newLevel])) {
        newExp -= BigInt(this.expData[newLevel])
        newLevel++
        burningBonus++
      } else {
        break
      }
    }
    return { newLevel, newExp, burningBonus }
  }

  /**
   * 計算使用票券後的結果
   * @param {number} currentLevel - 當前等級
   * @param {string} currentExpValue - 當前經驗值（帶千分位格式）
   * @param {Object} tickets - 票券物件
   * @param {string} burningMode - 燃燒模式
   * @returns {Object|null} 計算結果或 null
   */
  calculateTicketResult(currentLevel, currentExpValue, tickets, burningMode = null) {
    if (!currentLevel || !currentExpValue) return null

    let totalExp = BigInt(String(currentExpValue).replace(/,/g, ''))
    let level = currentLevel
    let burningBonus = 0
    let expTicketLeft = tickets.expTicket
    let expPlusTicketLeft = tickets.expPlusTicket

    let prevLevel = level;
    let prevExpTicket = expTicketLeft;
    let prevExpPlusTicket = expPlusTicketLeft;
    let prevBurningBonus = burningBonus;
    while ((expTicketLeft > 0 || expPlusTicketLeft > 0) && level < 300) {
      let canUseExpPlus = expPlusTicketLeft > 0 && this.ticketData['expPlusTicket'][level]
      let canUseExp = expTicketLeft > 0 && this.ticketData['expTicket'][level]
      if (!canUseExpPlus && !canUseExp) break

      let useExpPlus = canUseExpPlus && (!canUseExp || this.ticketData['expPlusTicket'][level].expPerTicket >= this.ticketData['expTicket'][level].expPerTicket)
      let ticketType = useExpPlus ? 'expPlusTicket' : 'expTicket'
      let expPerTicket = BigInt(this.ticketData[ticketType][level].expPerTicket)
      let ticketLeft = useExpPlus ? expPlusTicketLeft : expTicketLeft;

      // 計算升一級還需要多少票券
      let expNeeded = BigInt(this.expData[level]) - totalExp;
      let ticketsNeeded = expNeeded > 0n ? (expNeeded + expPerTicket - 1n) / expPerTicket : 0n;
      ticketsNeeded = ticketsNeeded > BigInt(ticketLeft) ? BigInt(ticketLeft) : ticketsNeeded;
      if (ticketsNeeded === 0n) ticketsNeeded = 1n; // 至少消耗一張

      // 如果票券不夠，全部加上去，break
      if (ticketsNeeded > BigInt(ticketLeft)) {
        totalExp += expPerTicket * BigInt(ticketLeft);
        if (useExpPlus) {
          expPlusTicketLeft = 0;
        } else {
          expTicketLeft = 0;
        }
        break;
      }

      // 消耗N張票券
      totalExp += expPerTicket * ticketsNeeded;
      if (useExpPlus) {
        expPlusTicketLeft -= Number(ticketsNeeded);
      } else {
        expTicketLeft -= Number(ticketsNeeded);
      }

      let leveledUp = false;
      let levelBefore = level;
      let burningBonusBefore = burningBonus;
      let burningJump = 0;
      // 如果經驗足夠升一級
      if (level < 300 && totalExp >= BigInt(this.expData[level])) {
        // 升一級（消耗經驗，消耗N張票券）
        totalExp -= BigInt(this.expData[level]);
        level++;
        leveledUp = true;
        // 如果有燃燒模式且等級<260，直接跳等（不消耗票券，每級都扣經驗，不能超過260）
        if (burningMode && level < 260) {
          const burningLevels = burningMode === 'burning1+2' ? 2 : 4;
          let jumpTo = Math.min(level + burningLevels, 260);
          burningJump = jumpTo - level;
          burningBonus += burningJump;
          level = jumpTo;
        }
        if (level >= 260) burningMode = null;
      }
      // 若等級有提升，log本次消耗
      if (leveledUp) {
        const usedExpTicket = prevExpTicket - expTicketLeft;
        const usedExpPlusTicket = prevExpPlusTicket - expPlusTicketLeft;
        let usedType = ticketType;
        let usedCount = usedType === 'expPlusTicket' ? usedExpPlusTicket : usedExpTicket;
        console.log(`[票券消耗] 類型:${usedType}，消耗:${usedCount}，等級:${prevLevel}→${level}（燃燒+${burningBonus - prevBurningBonus}），剩餘expTicket:${expTicketLeft}，剩餘expPlusTicket:${expPlusTicketLeft}`);
        // 重設本次提升起點
        prevLevel = level;
        prevExpTicket = expTicketLeft;
        prevExpPlusTicket = expPlusTicketLeft;
        prevBurningBonus = burningBonus;
      }
    }

    // 最後再做一次經驗值溢出升級，確保所有經驗值都正確升級
    while (level < 300 && totalExp >= BigInt(this.expData[level])) {
      totalExp -= BigInt(this.expData[level])
      level++
    }

    return {
      level: level,
      exp: Number(totalExp),
      levelExp: this.expData[level],
      percentage: (Number(totalExp) / this.expData[level]) * 100,
      burningBonus: burningBonus
    }
  }

  /**
   * 計算單一類型票券的結果
   * @param {number} level - 當前等級
   * @param {number} currentExp - 當前經驗值
   * @param {number} ticketCount - 票券數量
   * @param {string} ticketType - 票券類型 ('expTicket' 或 'expPlusTicket')
   * @param {string} burningMode - 燃燒模式
   * @returns {Object|null} 計算結果
   */
  calculateSingleTicketType(level, currentExp, ticketCount, ticketType, burningMode = null, maxLevel = 300) {
    if (!this.ticketData[ticketType][level]) {
      return null // 該等級無法使用此類型票券
    }

    let remainingTickets = ticketCount
    let newLevel = level
    let newExp = currentExp
    let burningBonus = 0
    let usedTickets = 0

    while (remainingTickets > 0 && newLevel < 300) {
      const levelData = this.ticketData[ticketType][newLevel]
      if (!levelData) break

      const levelExpRequired = this.expData[newLevel]
      const expNeeded = levelExpRequired - newExp
      const ticketsNeeded = Math.ceil(expNeeded / levelData.expPerTicket)

      // 處理燃燒模式
      if (burningMode && newLevel < 259) {
        // 計算升級到下一級所需的票券
        if (remainingTickets >= ticketsNeeded) {
          // 使用票券升級
          usedTickets += ticketsNeeded
          remainingTickets -= ticketsNeeded
          
          // 升級到下一級
          newLevel++
          newExp = 0

          // 套用燃燒效果
          const burningLevels = burningMode === 'burning1+2' ? 2 : 4
          burningBonus += burningLevels
          newLevel += burningLevels

          // 如果達到或超過260，設為260並切換到一般模式
          if (newLevel >= 260) {
            newLevel = 260
            // 如果還有剩餘票券，從260級開始消耗
            if (remainingTickets > 0) {
              const result = this.calculateSingleTicketType(260, 0, remainingTickets, ticketType, null)
              if (result) {
                newLevel = result.level
                newExp = result.exp
                usedTickets += result.ticketsUsed
              }
            }
            break
          }
        } else {
          // 票券不足以升級，全部換成經驗值
          newExp += remainingTickets * levelData.expPerTicket
          usedTickets += remainingTickets
          remainingTickets = 0
        }
      } else {
        // 一般模式（無燃燒）
        const expPerTicket = levelData.expPerTicket
        const totalExpFromTickets = remainingTickets * expPerTicket
        newExp += totalExpFromTickets
        usedTickets += remainingTickets
        remainingTickets = 0

        // 處理經驗值溢出
        while (newLevel < maxLevel && newExp >= this.expData[newLevel]) {
          newExp -= this.expData[newLevel]
          newLevel++
        }
      }
    }

    return {
      level: newLevel,
      exp: newExp,
      burningBonus: burningBonus,
      ticketsUsed: usedTickets
    }
  }

  /**
   * 檢查等級是否可以使用票券
   * @param {number} level - 等級
   * @param {string} ticketType - 票券類型
   * @returns {boolean} 是否可以使用
   */
  canUseTicket(level, ticketType) {
    return !!this.ticketData[ticketType][level]
  }

  /**
   * 取得票券資訊
   * @param {number} level - 等級
   * @param {string} ticketType - 票券類型
   * @returns {Object|null} 票券資訊
   */
  getTicketInfo(level, ticketType) {
    return this.ticketData[ticketType][level] || null
  }

  /**
   * 修正等級輸入，只允許整數且最大 299
   * @param {number|string} level - 使用者輸入的等級
   * @returns {number|null} 修正後的等級，無效則回傳 null
   */
  sanitizeLevelInput(level) {
    if (level === null || level === undefined || level === '') return null
    let sanitized = parseInt(level, 10)
    if (isNaN(sanitized)) return null
    if (sanitized > 299) sanitized = 299
    return sanitized
  }

  /**
   * 將經驗值字串去除非數字與逗號，並格式化
   * 同時回傳經驗值和對應的百分比
   * @param {number} level - 當前等級
   * @param {string} expValue - 經驗值字串
   * @returns {{formattedExp: string, percentage: number}} 格式化後的經驗值和百分比
   */
  parseAndFormatExpValue(level, expValue) {
    if (!expValue) return { formattedExp: '0', percentage: 0 };
    if (!level) return { formattedExp: '0', percentage: 0 };

    // 移除所有非數字和逗號的字符
    const numericValue = expValue.replace(/[^\d,]/g, '').replace(/,/g, '');
    if (!numericValue) return { formattedExp: '0', percentage: 0 };

    const value = parseInt(numericValue);
    const formattedExp = this.formatNumber(value);
    
    // 計算對應的百分比
    const levelExp = this.getLevelExp(level);
    const percentage = value >= levelExp ? 100 : (value / levelExp) * 100;

    return {
      formattedExp,
      percentage: Math.min(100, Math.max(0, percentage))
    };
  }

  /**
   * 百分比轉經驗值，並回傳格式化字串和精確的百分比
   * @param {number} level - 等級
   * @param {number} percentage - 百分比
   * @returns {{formattedExp: string, percentage: number}} 格式化後的經驗值和精確的百分比
   */
  percentageToExp(level, percentage) {
    if (!level || percentage === null || percentage === undefined) {
      return { formattedExp: '0', percentage: 0 };
    }

    // 確保百分比在有效範圍內
    const validPercentage = Math.min(Math.max(percentage, 0), 100);
    
    // 使用 BigInt 計算以提高精確度
    const levelExp = BigInt(this.getLevelExp(level));
    const expValue = Number((levelExp * BigInt(Math.round(validPercentage * 100))) / BigInt(10000));
    
    return {
      formattedExp: this.formatNumber(expValue),
      percentage: validPercentage
    };
  }

  /**
   * 經驗值轉百分比，並回傳格式化的經驗值和精確的百分比
   * @param {number} level - 等級
   * @param {string} expValue - 經驗值字串
   * @returns {{formattedExp: string, percentage: number}} 格式化後的經驗值和精確的百分比
   */
  expToPercentage(level, expValue) {
    if (!level || !expValue) return { formattedExp: '0', percentage: 0 };

    // 移除所有非數字和逗號的字符
    const cleanExp = expValue.replace(/[^\d,]/g, '').replace(/,/g, '');
    if (!cleanExp) return { formattedExp: '0', percentage: 0 };

    const value = BigInt(cleanExp);
    const levelExp = BigInt(this.getLevelExp(level));
    
    // 使用 BigInt 計算以提高精確度
    const percentage = Number((value * BigInt(10000)) / levelExp) / 100;
    
    return {
      formattedExp: this.formatNumber(Number(value)),
      percentage: Math.min(100, Math.max(0, percentage))
    };
  }

  /**
   * 驗證並格式化經驗值輸入
   * @param {number} level - 等級
   * @param {string} expValue - 經驗值字串
   * @returns {{formattedExp: string, percentage: number}} 格式化後的經驗值和百分比
   */
  validateAndFormatExp(level, expValue) {
    const result = this.parseAndFormatExpValue(level, expValue);
    const levelExp = this.getLevelExp(level);
    
    // 如果經驗值超過當前等級所需經驗，調整為100%
    if (parseInt(result.formattedExp.replace(/,/g, '')) >= levelExp) {
      return {
        formattedExp: this.formatNumber(levelExp),
        percentage: 100
      };
    }
    
    return result;
  }

  /**
   * 驗證並格式化百分比輸入
   * @param {number} level - 等級
   * @param {number} percentage - 百分比
   * @returns {{formattedExp: string, percentage: number}} 格式化後的經驗值和百分比
   */
  validateAndFormatPercentage(level, percentage) {
    const validPercentage = Math.min(Math.max(percentage || 0, 0), 100);
    return this.percentageToExp(level, validPercentage);
  }

  /**
   * 驗證秘藥/票券數量，回傳大於等於0的整數
   * @param {number|string} count
   * @returns {number}
   */
  validatePotionCount(count) {
    let n = parseInt(count, 10)
    if (isNaN(n) || n < 0) return 0
    return n
  }

  validateTicketCount(count) {
    return this.validatePotionCount(count)
  }

  /**
   * 增加數量
   * @param {number|string} count
   * @returns {number}
   */
  increaseCount(count) {
    let n = parseInt(count, 10)
    if (isNaN(n) || n < 0) n = 0
    return n + 1
  }

  /**
   * 減少數量（不小於0）
   * @param {number|string} count
   * @returns {number}
   */
  decreaseCount(count) {
    let n = parseInt(count, 10)
    if (isNaN(n) || n <= 0) return 0
    return n - 1
  }
} 